import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { trackFormSubmit } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validation functions
const validateEmail = (email) => {
  // Check if email is empty
  if (!email) return { valid: false, message: 'Email is required' };
  
  // Comprehensive email validation
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  // Basic format check
  if (!emailRegex.test(email)) {
    return { valid: false, message: 'Please enter a valid email address' };
  }
  
  // Check for common typos
  const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
  const domain = email.split('@')[1]?.toLowerCase();
  
  // Check for suspicious patterns
  if (email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    return { valid: false, message: 'Invalid email format' };
  }
  
  // Check for garbage entries (too many numbers in username)
  const username = email.split('@')[0];
  const numberCount = (username.match(/\d/g) || []).length;
  if (numberCount > username.length * 0.7) {
    return { valid: false, message: 'Email appears to be invalid' };
  }
  
  return { valid: true, message: '' };
};

const validatePhone = (phone) => {
  // Check if phone is empty
  if (!phone) return { valid: false, message: 'Phone number is required' };
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Check if contains only digits
  if (!/^\d+$/.test(digitsOnly)) {
    return { valid: false, message: 'Phone number must contain only digits' };
  }
  
  // Check minimum length (10 digits for most countries)
  if (digitsOnly.length < 10) {
    return { valid: false, message: 'Phone number must be at least 10 digits' };
  }
  
  // Check maximum length (15 digits is international standard)
  if (digitsOnly.length > 15) {
    return { valid: false, message: 'Phone number is too long' };
  }
  
  // Check for suspicious patterns (all same digit, sequential, etc.)
  const allSameDigit = /^(\d)\1+$/.test(digitsOnly);
  if (allSameDigit) {
    return { valid: false, message: 'Phone number appears to be invalid' };
  }
  
  // Check for sequential numbers (1234567890, 0123456789)
  const isSequential = /01234567890|12345678901|23456789012|34567890123|45678901234|56789012345|67890123456|78901234567|89012345678|90123456789/.test(digitsOnly);
  if (isSequential) {
    return { valid: false, message: 'Phone number appears to be invalid' };
  }
  
  return { valid: true, message: '' };
};

const ContactSection = ({ preSelectedCountry = '' }) => {
  const { toast } = useToast();
  const [sectionContent, setSectionContent] = useState({
    title: 'Get In Touch',
    subtitle: 'Ready to start your visa journey? Contact us today for a free consultation'
  });
  const [backgroundSettings, setBackgroundSettings] = useState({
    contact_image: '',
    contact_opacity: 0.5
  });
  const [contactInfo, setContactInfo] = useState({
    phone: '+91 98765 43210',
    email: 'info@visaasure.com',
    address: 'Office 301, Raheja Chambers, Nariman Point, Mumbai - 400021, India',
    whatsapp: '+91 98765 43210',
    hours: 'Mon-Sat: 10:00 AM - 7:00 PM'
  });
  const [services, setServices] = useState([]);
  const [callbackSlots, setCallbackSlots] = useState([]);  // NEW: Store callback slots
  const [availableSlots, setAvailableSlots] = useState([]);  // NEW: Filtered slots based on selected date
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    visa_type: '',
    country: preSelectedCountry,
    custom_country: '',
    schengen_country: '',  // NEW: For Schengen country selection
    travel_date: '',
    num_applicants: 1,
    callback_date: '',
    callback_slot: '',  // CHANGED: from callback_time to callback_slot
    message: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [showSchengenDropdown, setShowSchengenDropdown] = useState(false);

  // Update country when preSelectedCountry changes
  useEffect(() => {
    if (preSelectedCountry) {
      setFormData(prev => ({
        ...prev,
        country: preSelectedCountry
      }));
    } else {
      // Clear country if no pre-selection
      setFormData(prev => ({
        ...prev,
        country: ''
      }));
    }
  }, [preSelectedCountry]);

  useEffect(() => {
    fetchSectionContent();
    fetchContactInfo();
    fetchServices();
    fetchBackgroundSettings();
    fetchCallbackSlots();  // NEW: Fetch callback slots
  }, []);

  const fetchSectionContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      const contactData = response.data.find(item => item.section_name === 'contact');
      if (contactData && contactData.content) {
        setSectionContent(contactData.content);
      }
    } catch (error) {
      console.error('Failed to fetch section content:', error);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${API}/schengen-countries`);
      setSchengenCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching Schengen countries:', error);
      setSchengenCountries([]);
    }
  };

  const fetchBackgroundSettings = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      let bgData = response.data.find(item => item.section_name === 'backgrounds_v2');
      if (!bgData) {
        bgData = response.data.find(item => item.section_name === 'backgrounds');
      }
      if (bgData && bgData.content) {
        setBackgroundSettings({
          contact_image: bgData.content.contact_image || '',
          contact_opacity: bgData.content.contact_opacity || 0.5
        });
      }
    } catch (error) {
      console.error('Failed to fetch background settings:', error);
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings/contact_info`);
      setContactInfo(response.data.setting_value);
    } catch (error) {
      console.error('Failed to fetch contact info:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCallbackSlots = async () => {
    try {
      const response = await axios.get(`${API}/callback-slots/active`);
      setCallbackSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch callback slots:', error);
    }
  };

  // Fetch available slots for selected date (includes capacity check)
  const fetchAvailableSlotsForDate = async (date) => {
    try {
      const response = await axios.get(`${API}/callback-slots/available-for-date?date=${date}`);
      const slots = response.data;
      
      // Further filter by current time if date is today
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      const isToday = selectedDate.getTime() === today.getTime();
      
      if (isToday) {
        const now = new Date();
        const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
        
        const futureSlots = slots.filter(slot => {
          const [slotHour, slotMinute] = slot.start_time.split(':').map(Number);
          const slotTimeInMinutes = slotHour * 60 + slotMinute;
          // Only show slots that haven't started yet (with 15 min buffer)
          return slotTimeInMinutes > currentTimeInMinutes + 15;
        });
        
        return futureSlots;
      }
      
      return slots;
    } catch (error) {
      console.error('Failed to fetch available slots for date:', error);
      return [];
    }
  };

  // Filter slots based on selected callback date WITH CAPACITY CHECK
  useEffect(() => {
    if (formData.callback_date) {
      // Fetch slots with capacity check from backend
      fetchAvailableSlotsForDate(formData.callback_date).then(slots => {
        setAvailableSlots(slots);
        
        // Reset callback_slot if current selection is not available
        if (formData.callback_slot) {
          const isCurrentSlotAvailable = slots.some(slot => slot.slot_label === formData.callback_slot);
          if (!isCurrentSlotAvailable) {
            setFormData(prev => ({ ...prev, callback_slot: '' }));
          }
        }
      });
    } else {
      setAvailableSlots([]);
    }
  }, [formData.callback_date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone number - only allow digits
    if (name === 'phone') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 15 digits (international max)
      const limitedValue = digitsOnly.slice(0, 15);
      
      setFormData({
        ...formData,
        [name]: limitedValue
      });
      
      // Show real-time validation feedback
      if (limitedValue.length > 0 && limitedValue.length < 10) {
        // Could show a subtle warning here if needed
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      toast({
        title: "Invalid Email",
        description: emailValidation.message,
        variant: "destructive"
      });
      return;
    }
    
    // Validate phone
    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.valid) {
      toast({
        title: "Invalid Phone Number",
        description: phoneValidation.message,
        variant: "destructive"
      });
      return;
    }
    
    // Validate Schengen country if Schengen is selected
    if (showSchengenDropdown && !formData.schengen_country) {
      toast({
        title: "Schengen Country Required",
        description: "Please select a specific Schengen country",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Determine final country: use Schengen country if selected, otherwise use custom or primary
      let finalCountry;
      if (showSchengenDropdown && formData.schengen_country) {
        finalCountry = formData.schengen_country;
      } else if (formData.country === 'Other') {
        finalCountry = formData.custom_country;
      } else {
        finalCountry = formData.country;
      }
      
      // Create lead data matching the Lead model
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.visa_type,
        destination_country: finalCountry,
        num_applicants: parseInt(formData.num_applicants) || 1,
        message: formData.message,
        description: `Travel Date: ${formData.travel_date}, Callback: ${formData.callback_date} ${formData.callback_slot}`,
        source: 'website',
        temperature: 'warm'
      };
      
      const response = await axios.post(`${API}/leads`, leadData);
      
      // Success - lead created (could be new or duplicate)
      console.log('Lead submitted successfully:', response.data);
      
      // Track form submission
      trackFormSubmit('lead_form', {
        service_type: leadData.service_type,
        destination_country: leadData.destination_country,
        source: 'website'
      });
      
      // Store submitted data for confirmation
      setSubmittedData({
        ...formData,
        country: finalCountry
      });
      setShowConfirmation(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        visa_type: '',
        country: '',
        custom_country: '',
        travel_date: '',
        num_applicants: 1,
        callback_date: '',
        callback_slot: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to submit lead:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 422) {
          // Validation error from backend
          let errorMessage = 'Please check your input and try again.';
          
          // Extract validation error details
          if (errorData.detail && Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            const validationErrors = errorData.detail.map(err => {
              const field = err.loc[err.loc.length - 1];
              return `${field}: ${err.msg}`;
            }).join(', ');
            errorMessage = validationErrors;
          } else if (errorData.detail && typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
          
          toast({
            title: "Invalid Input",
            description: errorMessage,
            variant: "destructive"
          });
        } else if (status === 500) {
          // Server error
          toast({
            title: "Server Error",
            description: "Something went wrong on our end. Please try again later.",
            variant: "destructive"
          });
        } else {
          // Other errors
          toast({
            title: "Error",
            description: errorData.detail || "Failed to submit request. Please try again.",
            variant: "destructive"
          });
        }
      } else if (error.request) {
        // Request made but no response
        toast({
          title: "Connection Error",
          description: "Unable to reach the server. Please check your internet connection.",
          variant: "destructive"
        });
      } else {
        // Something else happened
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <section id="contact" className="contact-section" style={{
      backgroundImage: backgroundSettings.contact_image ? `url(${backgroundSettings.contact_image})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative'
    }}>
      {backgroundSettings.contact_image && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: `rgba(255, 249, 242, ${backgroundSettings.contact_opacity})`,
          pointerEvents: 'none'
        }} />
      )}
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="section-header">
          <h2 className="heading-1">{sectionContent.title}</h2>
          <p className="body-large section-subtitle">
            {sectionContent.subtitle}
          </p>
        </div>

        <div className="contact-grid">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <h3 className="heading-2 form-title">Request Free Consultation</h3>
              
              <div className="form-group">
                <label htmlFor="name" className="form-label body-small">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label body-small">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label body-small">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="+91 XXXXX XXXXX"
                  pattern="[0-9]{10,15}"
                  title="Please enter a valid phone number (10-15 digits)"
                  inputMode="numeric"
                />
              </div>

              <div className="form-group">
                <label htmlFor="visa_type" className="form-label body-small">Visa Type *</label>
                <select
                  id="visa_type"
                  name="visa_type"
                  value={formData.visa_type}
                  onChange={handleChange}
                  required
                  className="form-input"
                >
                  <option value="">Select visa type</option>
                  {services.map(service => (
                    <option key={service.id} value={service.title}>
                      {service.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="country" className="form-label body-small">
                  Select Country * 
                  {preSelectedCountry && <span className="pre-selected-badge">Pre-selected</span>}
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, country: value, custom_country: '', schengen_country: '' });
                    
                    // If Schengen is selected, fetch and show Schengen countries
                    if (value.toLowerCase() === 'schengen') {
                      await fetchSchengenCountries();
                      setShowSchengenDropdown(true);
                    } else {
                      setShowSchengenDropdown(false);
                    }
                  }}
                  required
                  className={`form-input ${preSelectedCountry ? 'pre-selected-field' : ''}`}
                >
                  <option value="">Select destination country</option>
                  <option value="USA">USA</option>
                  <option value="UK">UK</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Germany">Germany</option>
                  <option value="Schengen">Schengen (Europe)</option>
                  <option value="UAE">UAE</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {showSchengenDropdown && (
                <div className="form-group">
                  <label htmlFor="schengen_country" className="form-label body-small">Select Schengen Country *</label>
                  <select
                    id="schengen_country"
                    name="schengen_country"
                    value={formData.schengen_country}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select Schengen country</option>
                    {schengenCountries.map((country, index) => (
                      <option key={index} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.country === 'Other' && (
                <div className="form-group">
                  <label htmlFor="custom_country" className="form-label body-small">Specify Country *</label>
                  <input
                    type="text"
                    id="custom_country"
                    name="custom_country"
                    value={formData.custom_country}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Enter country name"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="travel_date" className="form-label body-small">Tentative Date of Travel *</label>
                <input
                  type="date"
                  id="travel_date"
                  name="travel_date"
                  value={formData.travel_date}
                  onChange={handleChange}
                  required
                  className="form-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="num_applicants" className="form-label body-small">Number of Applicants *</label>
                <input
                  type="number"
                  id="num_applicants"
                  name="num_applicants"
                  value={formData.num_applicants}
                  onChange={handleChange}
                  required
                  className="form-input"
                  min="1"
                  max="20"
                  placeholder="Enter number of applicants"
                />
              </div>

              <div className="form-group">
                <label htmlFor="callback_date" className="form-label body-small">Preferred Callback Date *</label>
                <input
                  type="date"
                  id="callback_date"
                  name="callback_date"
                  value={formData.callback_date}
                  onChange={handleChange}
                  required
                  className="form-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="callback_time" className="form-label body-small">Preferred Callback Time *</label>
                <select
                  id="callback_slot"
                  name="callback_slot"
                  value={formData.callback_slot}
                  onChange={handleChange}
                  required
                  className="form-input"
                  disabled={!formData.callback_date || availableSlots.length === 0}
                >
                  <option value="">
                    {!formData.callback_date 
                      ? 'Please select callback date first' 
                      : availableSlots.length === 0 
                        ? 'No slots available for selected date/time'
                        : 'Select time slot'
                    }
                  </option>
                  {availableSlots.map(slot => (
                    <option key={slot.id} value={slot.slot_label}>
                      {slot.slot_label}
                      {slot.available_capacity !== null && slot.available_capacity !== undefined 
                        ? ` (${slot.available_capacity} spots left)` 
                        : ''
                      }
                    </option>
                  ))}
                </select>
                {!formData.callback_date && (
                  <p className="body-small" style={{ marginTop: '4px', color: '#64748b' }}>
                    Select a callback date to see available time slots
                  </p>
                )}
                {formData.callback_date && availableSlots.length === 0 && (
                  <p className="body-small" style={{ marginTop: '4px', color: '#dc2626' }}>
                    No available slots for this date. Please choose a different date.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label body-small">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="form-input"
                  placeholder="Tell us about your visa requirements..."
                ></textarea>
              </div>

              <button type="submit" className="btn-primary submit-btn">
                <span>Submit Request</span>
                <Send size={16} />
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="contact-info-wrapper">
            <div className="contact-card">
              <div className="contact-icon">
                <Phone size={24} />
              </div>
              <h3 className="heading-3">Phone</h3>
              <p className="body-medium">{contactInfo.phone}</p>
              <a href={`tel:${contactInfo.phone}`} className="btn-secondary contact-btn">
                Call Now
              </a>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <MessageCircle size={24} />
              </div>
              <h3 className="heading-3">WhatsApp</h3>
              <p className="body-medium">{contactInfo.whatsapp}</p>
              <a 
                href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary contact-btn"
              >
                Chat Now
              </a>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <Mail size={24} />
              </div>
              <h3 className="heading-3">Email</h3>
              <p className="body-medium">{contactInfo.email}</p>
              <a href={`mailto:${contactInfo.email}`} className="btn-secondary contact-btn">
                Send Email
              </a>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <MapPin size={24} />
              </div>
              <h3 className="heading-3">Office</h3>
              <p className="body-medium">{contactInfo.address}</p>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <Clock size={24} />
              </div>
              <h3 className="heading-3">Hours</h3>
              <p className="body-medium">{contactInfo.hours}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && submittedData && (
        <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="success-icon">✓</div>
              <h2 className="heading-2">Request Submitted Successfully!</h2>
            </div>
            
            <div className="confirmation-details">
              <h3 className="heading-3">Your Submission Details:</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{submittedData.name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{submittedData.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{submittedData.phone}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Visa Type:</span>
                  <span className="detail-value">{submittedData.visa_type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Country:</span>
                  <span className="detail-value">{submittedData.country}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Travel Date:</span>
                  <span className="detail-value">{submittedData.travel_date}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Applicants:</span>
                  <span className="detail-value">{submittedData.num_applicants}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Callback Date:</span>
                  <span className="detail-value">{submittedData.callback_date}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Callback Time:</span>
                  <span className="detail-value">{submittedData.callback_slot}</span>
                </div>
              </div>
            </div>

            <div className="confirmation-message">
              <p className="body-large">
                <strong>A visa expert from Visaasure will connect with you shortly or as per your selected schedule.</strong>
              </p>
              <p className="body-medium">Thank you for your enquiry!</p>
            </div>

            <button onClick={() => setShowConfirmation(false)} className="btn-primary">
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContactSection;